// 金融機関の明細CSVインポートに共通する定義と処理。
// 設定画面のCSVタブと、口座カードからのインポートの両方がここを参照する。
import type { Account } from '@zeni/shared'
import type { TranslationKey } from '../i18n/translations'
import { csvApi } from './api'
import { decodeCsvBuffer, normalizeForMatch, type CsvCategoryRef } from './csv-common'
import { parsePayPayCardCsv } from './paypay-card-csv'
import { parseRakutenCsv } from './rakuten-csv'
import { parseSmbcCardCsv } from './smbc-card-csv'
import { parseSbiNetBankCsv } from './sbi-net-bank-csv'
import { parseGmoAozoraBankCsv } from './gmo-aozora-bank-csv'
import { parseRakutenBankCsv } from './rakuten-bank-csv'
import { parsePayPayBankCsv } from './paypay-bank-csv'
import { parseViewCardCsv } from './view-card-csv'

export type CsvFormat = 'generic' | 'rakuten' | 'paypay_card' | 'view_card' | 'smbc_card' | 'sbi_net_bank' | 'gmo_aozora_bank' | 'rakuten_bank' | 'paypay_bank'

// 金融機関フォーマット（汎用以外）。CSVに口座情報が無いため取り込み先の口座をユーザーに選ばせる。
export type InstitutionFormat = Exclude<CsvFormat, 'generic'>

export const INSTITUTION_FORMATS: readonly InstitutionFormat[] = [
  'rakuten', 'paypay_card', 'view_card', 'smbc_card', 'sbi_net_bank', 'gmo_aozora_bank', 'rakuten_bank',
  'paypay_bank',
]

// フォーマット名（セレクトの選択肢・口座カードのボタン表示に使う）
export const FORMAT_LABEL_KEYS: Record<InstitutionFormat, TranslationKey> = {
  rakuten: 'csv_format_rakuten',
  paypay_card: 'csv_format_paypay_card',
  view_card: 'csv_format_view_card',
  smbc_card: 'csv_format_smbc_card',
  sbi_net_bank: 'csv_format_sbi_net_bank',
  gmo_aozora_bank: 'csv_format_gmo_aozora_bank',
  rakuten_bank: 'csv_format_rakuten_bank',
  paypay_bank: 'csv_format_paypay_bank',
}

// フォーマットごとの説明文（口座選択欄の下に表示する）
export const FORMAT_NOTE_KEYS: Record<InstitutionFormat, TranslationKey> = {
  rakuten: 'csv_import_rakuten_note',
  paypay_card: 'csv_import_paypay_card_note',
  view_card: 'csv_import_view_card_note',
  smbc_card: 'csv_import_smbc_card_note',
  sbi_net_bank: 'csv_import_sbi_net_bank_note',
  gmo_aozora_bank: 'csv_import_gmo_aozora_bank_note',
  rakuten_bank: 'csv_import_rakuten_bank_note',
  paypay_bank: 'csv_import_paypay_bank_note',
}

// フォーマットごとに対応する金融機関のキーワード。口座の金融機関名（未設定なら口座名）に
// いずれかを含む口座だけを取り込み先の候補に絞る。
// 口座の institution は金融機関マスタから補完される自由入力なので、表記ゆれを拾えるよう
// 複数のキーワードを持たせ、NFKC正規化＋大文字化してから突き合わせる。
// 「あおぞら」「三井住友」のような他行と共通の部分文字列は、GMOあおぞら銀行と
// あおぞら銀行、三井住友カードと三井住友銀行を混同するので単独では使わない。
const FORMAT_INSTITUTION_KEYWORDS: Record<InstitutionFormat, readonly string[]> = {
  rakuten: ['楽天カード', 'RAKUTEN CARD'],
  paypay_card: ['PayPayカード', 'PayPay Card'],
  view_card: ['ビューカード', 'ビュー・カード', 'VIEW CARD', 'JRE CARD'],
  smbc_card: ['三井住友カード', 'SMBCカード', 'Vpass'],
  sbi_net_bank: ['ドコモSMTB', 'SMTBネット', '住信SBI', 'スミシンSBI', 'NEOBANK'],
  gmo_aozora_bank: ['GMOあおぞら', 'あおぞらネット'],
  rakuten_bank: ['楽天銀行', 'RAKUTEN BANK'],
  paypay_bank: ['PayPay銀行', 'PayPay Bank', 'ジャパンネット銀行'],
}

// 口座がそのフォーマットの取り込み先になり得るか。
export function matchesFormat(account: Account, csvFormat: InstitutionFormat): boolean {
  const target = normalizeForMatch(`${account.institution ?? ''} ${account.name}`)
  return FORMAT_INSTITUTION_KEYWORDS[csvFormat].some((k) => target.includes(normalizeForMatch(k)))
}

// その口座に取り込めるフォーマット。通常は1件だが、金融機関名の表記によっては
// 複数一致し得るのでユーザーに選ばせる前提で配列を返す。
export function formatsForAccount(account: Account): InstitutionFormat[] {
  return INSTITUTION_FORMATS.filter((f) => matchesFormat(account, f))
}

export type ImportRow = {
  date: string; type: 'income' | 'expense' | 'transfer'; category: string;
  description: string; amount: number; accountId: string;
  toAccountId?: string; currency?: string; note?: string;
  /** 集計対象外。Zeni 自身の CSV を取り込み直したときに保つ */
  excluded?: boolean;
}

// 金融機関のCSVは Shift-JIS / UTF-8 のどちらもあり得るので ArrayBuffer から判定してデコードする。
export async function parseInstitutionCsvFile(
  file: File,
  csvFormat: InstitutionFormat,
  accountId: string,
  categories: CsvCategoryRef[],
): Promise<ImportRow[]> {
  const text = decodeCsvBuffer(await file.arrayBuffer())
  const parse =
    csvFormat === 'rakuten' ? parseRakutenCsv
    : csvFormat === 'paypay_card' ? parsePayPayCardCsv
    : csvFormat === 'view_card' ? parseViewCardCsv
    : csvFormat === 'smbc_card' ? parseSmbcCardCsv
    : csvFormat === 'sbi_net_bank' ? parseSbiNetBankCsv
    : csvFormat === 'rakuten_bank' ? parseRakutenBankCsv
    : csvFormat === 'paypay_bank' ? parsePayPayBankCsv
    : parseGmoAozoraBankCsv
  return parse(text, accountId, categories).rows
}

// /transactions/import は1回あたり最大1000行。バッチに分割して送信し件数を合算する。
export async function importRowsInChunks(
  token: string,
  rows: ImportRow[],
): Promise<{ imported: number; skipped: number }> {
  let imported = 0
  let skipped = 0
  for (let i = 0; i < rows.length; i += 1000) {
    const res = await csvApi.importTransactions(token, rows.slice(i, i + 1000))
    imported += res.data.imported
    skipped += res.data.skipped
  }
  return { imported, skipped }
}
